import { useQuery } from '@tanstack/react-query'
import { Building2, ChevronRight } from 'lucide-react'
import { Link, Navigate } from 'react-router'

import { consulta, pedir } from '../api/nucleo'
import { espaciosDe, useSalir, useSesion } from '../api/sesion'
import type { Espacio, Lista } from '../api/tipos'
import { Escribenos } from '../componentes/Ayuda'
import { LogoDeMarca } from '../marca/marca'
import { Boton, Cargando, Tarjeta } from '../componentes/ui'

/**
 * Nivel 0: con qué espacio de trabajo se trabaja. Casi todo el mundo tiene uno solo y
 * no ve esta pantalla nunca; quien tiene varios —una agencia, o alguien del equipo—
 * elige, y a partir de ahí el espacio va en la dirección.
 */
export const Espacios = () => {
  const { usuario, cargando } = useSesion()
  const salir = useSalir()
  const esEquipo = usuario?.rol === 'superadmin'

  // El superadmin no lleva la lista dentro del usuario: los ve todos (§4).
  const { data: todos, isPending } = useQuery({
    queryKey: ['espacios-todos'],
    queryFn: async () => (await pedir<Lista<Espacio>>(`/clientes${consulta({ limit: 100, sort: 'nombre', depth: 0 })}`)).docs,
    enabled: esEquipo,
  })

  if (cargando || (esEquipo && isPending)) return <Cargando que="Buscando tus espacios" />

  const espacios = esEquipo ? (todos ?? []) : espaciosDe(usuario)
  if (espacios.length === 1 && espacios[0]) return <Navigate replace to={`/e/${espacios[0].id}`} />

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col justify-center px-4 py-12">
      <div className="mb-8">
        <LogoDeMarca tamano="grande" />
      </div>
      <p className="text-[13px] text-tenue">{usuario?.nombre || usuario?.email}</p>
      <h1 className="mt-1 font-display text-[26px] font-semibold tracking-tight">{espacios.length === 0 ? 'Todavía no tienes espacios' : 'Elige un espacio de trabajo'}</h1>
      {espacios.length === 0 ? (
        <p className="mt-3 text-sm text-tenue">
          Tu cuenta no está asociada a ningún espacio. <Escribenos contexto="sin-espacio">Escríbenos</Escribenos> y lo arreglamos.
        </p>
      ) : (
        <ul className="mt-6 flex flex-col gap-2">
          {espacios.map((espacio) => (
            <li key={espacio.id}>
              <Link className="group block" to={`/e/${espacio.id}`}>
                <Tarjeta className="flex items-center gap-3 !py-3.5 transition-colors group-hover:border-violeta-borde group-hover:bg-violeta-suave/40">
                  <span className="flex size-9 items-center justify-center rounded-lg bg-violeta-suave text-violeta">
                    <Building2 className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-medium">{espacio.nombre}</span>
                    <span className="block truncate text-[13px] text-tenue">{espacio.slug}</span>
                  </span>
                  <ChevronRight className="size-4 text-tenue transition-transform group-hover:translate-x-0.5 group-hover:text-violeta" />
                </Tarjeta>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-8">
        <Boton onClick={() => salir.mutate()} pequeno tipo="discreto">
          Salir
        </Boton>
      </div>
    </div>
  )
}
